import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

// ── Types ─────────────────────────────────────────────────────────────────────
interface FaqItem {
  title: string
  title1?: string
  title2?: string
  text0?: string
  text1?: string
  text2?: string
  text3?: string
  text4?: string
  text5?: string
  text6?: string
  text7?: string
  text8?: string
  ul?: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const BRAND = '#6eb446'

/** Returns only the text[n] values that are defined, in order. */
const getTexts = (faq: FaqItem): string[] =>
  [0, 1, 2, 3, 4, 5, 6, 7, 8]
    .map(i => (faq as FaqItem | undefined)[`text${i}`])
    .filter((v): v is string => Boolean(v))

// ── AccordionItem ─────────────────────────────────────────────────────────────
interface AccordionItemProps {
  faq: FaqItem
  index: number
  open: boolean
  onToggle: () => void
}

const AccordionItem: React.FC<AccordionItemProps> = ({ faq, index, open, onToggle }) => (
  <div
    style={{
      borderBottom: '1px solid #e2e8f0',
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <button
      onClick={onToggle}
      aria-expanded={open}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1.1rem 0',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        gap: '1rem',
      }}
    >
      <span style={{
        fontWeight: 600,
        fontSize: '0.95rem',
        color: open ? BRAND : '#1e293b',
        transition: 'color 0.2s',
        lineHeight: 1.4,
      }}>
        {faq.title}
      </span>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: open ? BRAND : '#f1f5f9',
          color: open ? '#fff' : '#64748b',
          fontSize: '1.1rem',
          fontWeight: 300,
          lineHeight: 1,
          transition: 'background-color 0.2s, color 0.2s, transform 0.25s',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          userSelect: 'none',
        }}
      >
        +
      </span>
    </button>

    {/* Content */}
    <div
      style={{
        maxHeight: open ? '1000px' : 0,
        overflow: 'hidden',
        transition: 'max-height 0.35s ease',
      }}
    >
      <div style={{ padding: '0 0.25rem 1.25rem', color: '#475569', fontSize: '0.9rem', lineHeight: 1.75 }}>
        {faq.ul ? (
          // Special layout: intro + two sub-lists
          <>
            <p style={{ margin: '0 0 1rem' }}>{faq.text0}</p>

            {faq.title1 && (
              <p style={{ fontWeight: 700, color: '#1e293b', margin: '0 0 0.5rem' }}>{faq.title1}</p>
            )}
            <ul style={{ margin: '0 0 1.25rem', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[faq.text1, faq.text2, faq.text3].filter(Boolean).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>

            {faq.title2 && (
              <p style={{ fontWeight: 700, color: '#1e293b', margin: '0 0 0.5rem' }}>{faq.title2}</p>
            )}
            <ul style={{ margin: 0, paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[faq.text4, faq.text5, faq.text6, faq.text7, faq.text8].filter(Boolean).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </>
        ) : (
          // Standard layout: sequential paragraphs
          getTexts(faq).map((text, i) => (
            <p key={i} style={{ margin: i === 0 ? 0 : '0.75rem 0 0' }}>{text}</p>
          ))
        )}
      </div>
    </div>
  </div>
)

// ── FAQS ──────────────────────────────────────────────────────────────────────
const FAQS: React.FC = () => {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggle = (i: number) => setOpenIndex(prev => (prev === i ? null : i))

  const faqs: FaqItem[] = [
    {
      title: t('faqs.title1'),
      text0: t('faqs.text1'),
    },
    {
      title: t('faqs.title2'),
      text0: t('faqs.text2'),
      text1: t('faqs.text2_1'),
    },
    {
      title: t('faqs.title3'),
      text0: t('faqs.text3'),
    },
    {
      title: t('faqs.title5'),
      text0: t('faqs.text5'),
      text1: t('faqs.text5_1'),
      text2: t('faqs.text5_2'),
      text3: t('faqs.text5_3'),
      text4: t('faqs.text5_4'),
    },
    {
      title: t('faqs.title6'),
      text0: t('faqs.text6'),
    },
    {
      title: t('faqs.title7'),
      text0: t('faqs.text7'),
      text1: t('faqs.text7_1'),
      text2: t('faqs.text7_2'),
    },
    {
      title: t('faqs.title8'),
      text0: t('faqs.text8'),
    },
    {
      title:  t('faqs.title9'),
      title1: t('faqs.title9_1'),
      title2: t('faqs.title9_2'),
      text0:  t('faqs.text9'),
      text1:  t('faqs.text9_1'),
      text2:  t('faqs.text9_2'),
      text3:  t('faqs.text9_3'),
      text4:  t('faqs.text9_4'),
      text5:  t('faqs.text9_5'),
      text6:  t('faqs.text9_6'),
      text7:  t('faqs.text9_7'),
      text8:  t('faqs.text9_8'),
      ul: true,
    },
    {
      title: t('faqs.title10'),
      text0: t('faqs.text10'),
      text1: t('faqs.text10_1'),
      text2: t('faqs.text10_2'),
    },
    {
      title: t('faqs.title11'),
      text0: t('faqs.text11'),
    },
    {
      title: t('faqs.title12'),
      text0: t('faqs.text12'),
      text1: t('faqs.text12_1'),
      text2: t('faqs.text12_2'),
      text3: t('faqs.text12_3'),
    },
    {
      title: t('faqs.title13'),
      text0: t('faqs.text13'),
    },
  ]

  return (
    <section style={{ backgroundColor: '#fff', borderBottom: '4px solid #000', padding: '5rem 1.5rem' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 14px',
            borderRadius: '9999px',
            backgroundColor: '#e8f5dc',
            color: BRAND,
            fontSize: '1.4rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '0.75rem',
          }}>
            {t('home.faqs')}
          </span>
          <h2 style={{
            margin: 0,
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            fontWeight: 800,
            color: '#1e293b',
          }}>
            
          </h2>
        </div>

        {/* Accordion */}
        <div style={{ borderTop: '1px solid #e2e8f0' }}>
          {faqs.map((faq, i) => (
            <AccordionItem
              key={i}
              index={i}
              faq={faq}
              open={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>

        {/* Help CTA */}
        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <a
            href="https://ayuda.labeldictate.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.875rem 2.5rem',
              borderRadius: '9999px',
              backgroundColor: BRAND,
              color: '#fff',
              fontWeight: 700,
              fontSize: '1rem',
              textDecoration: 'none',
              boxShadow: '0 8px 25px rgba(110,180,70,0.35)',
              transition: 'filter 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
          >
            {t('home.help')}
          </a>
        </div>

      </div>
    </section>
  )
}

export default FAQS
